import BaseForgeCustomizer from '../base-forge';
import HeaderWithEventHelper from '../../components/common/Header';
import SymantecCode from '../../components/password-reset/SymantecCode';
import BlockProdUserPasswordResetInSupportBanner from '../../components/password-reset/BlockPasswordResetForProdUserInSupport';
import {
  forgifyPasswordResetErrorContainer,
  forgifyPasswordResetRequirements
} from '../../utils/password-requirements-customizer';
import AmplitudeUtils from '../../utils/amplitude-utils';
import {
  getAnetUsername,
  isAnetUser,
  isProdUserInSupport
} from '../../utils/user-utils';
import {
  ERROR_KEYS,
  ERROR_MESSAGES
} from '../../constants';
import { createWrapper } from '../../utils/react-utils';
import { getLocalizedString } from '../../utils/i18n-utils';
import {
  userPasswordReset,
  shouldPromptForSymantecVIP as shouldPromptForSymantecVIPLambda,
  getTransformedError,
  isRetryableError,
  submitLegacyPasswordResetForm
} from '../../utils/password-reset-api';

import {
  getStateHandle,
  getOktaUserId,
  getOktaWidgetModel,
  startSpinnerAndHideForm,
  stopSpinner,
  stopSpinnerAndShowForm,
  getUsername,
  showFormErrors,
  isPasswordLengthValid,
  hideWidgetForm
} from '../../utils/widget-facade';

interface SymantecPromptPayload {
  username: string;
  stateHandle: string;
}

/**
 * Customizer for password reset completion screen (forgot password flow)
 * Handles both regular users and EPCS users with VIP code validation
 *
 * Controller: forgot-password
 * Form Name: reset-authenticator
 * Authenticator Key: okta_password
 */
export default class PasswordResetCompletionCustomizer extends BaseForgeCustomizer {
  protected shouldPromptForSymantec: boolean = false;
  protected symantecFieldIndex = 0;

  /**
   * Determines if this customizer should be applied
   * Detects password reset completion screen (forgot-password flow)
   */
  async shouldCustomize() {
    return (
      this.context?.controller === 'forgot-password' &&
      this.context?.formName === 'reset-authenticator' &&
      this.context?.authenticatorKey === 'okta_password' &&
      isAnetUser()
    );
  }

  /**
   * Main customization entry point
   */
  async customize() {
    startSpinnerAndHideForm();
    const prodUserInSupport = isProdUserInSupport();

    if (prodUserInSupport) {
      await super.customize();
      this.blockPasswordUpdateForProdUser();
      return;
    }

    AmplitudeUtils.logCustomEvent('PasswordReset::Landing', {
      username: getAnetUsername(),
    });
    await super.customize();

    await this.handleSymantecPrompt();
    this.customizePasswordResetButton();

    stopSpinnerAndShowForm();
  }

  protected forgifyController() {
    super.forgifyController();
    this.forgifyPasswordRequirements();
  }

  /**
   * Override error container to show custom error banner with mapped error messages
   */
  protected forgifyErrorContainer() {
    forgifyPasswordResetErrorContainer(
      this.renderForgeAndHideOktaComponent.bind(this)
    );
  }

  /**
   * Use V2 password requirements component with static requirements list
   * Positions the component directly under the new password input field
   */
  protected forgifyPasswordRequirements() {
    const { containerSelector } = this.getPasswordRequirementsSelector();
    forgifyPasswordResetRequirements(
      containerSelector,
      this.renderReactComponent,
      this.addToHideList.bind(this)
    );
  }

  protected getPasswordRequirementsSelector() {
    return {
      containerSelector: '[data-se="password-authenticator--rules"]',
      headerSelector: '.password-authenticator--heading',
      itemListSelector: '.password-authenticator--list',
    };
  }

  /**
   * Customize password reset button validation and submission
   */
  protected customizePasswordResetButton() {
    const model = getOktaWidgetModel();
    const originalValidate = model.validate.bind(model);

    // Add Symantec code validation
    model.validate = () => {
      const response = originalValidate();
      const symantecCodeValid = this.isSecurityCodeValid();
      if (response) {
        return response;
      }
      const isPasswordLengthValidValue = isPasswordLengthValid();
      if (!isPasswordLengthValidValue) {
        showFormErrors({ errorSummary: getLocalizedString('password.length.requirements.not.met') });
        return { 'credentials.passcode': 'error' };
      }
      return symantecCodeValid ? undefined : { 'symantec-token': 'error' };
    };

    this.onPasswordReset();
  }

  /**
   * Sets up error handling for password reset form submission
   * Handles Symantec validation errors and logs analytics events
   */
  protected setupPasswordResetErrorHandling() {
    const model = getOktaWidgetModel();

    // Handle errors by listening to error events once the button is clicked.
    // This helps in receiving a more "normalized" error payload
    // See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L385
    model.once('error', (_: any, error: any) => {
      if (this.isSymantecValidationError(error?.errorCode)) {
        this.clearPasswordResetForm();
      }
      else {
        this.clearSymantecCode();
      }
      AmplitudeUtils.logCustomEvent('PasswordReset::PasswordChangeFailed', {
        username: getAnetUsername(),
        errorMessage: error?.errorSummary ?? ERROR_MESSAGES[ERROR_KEYS.GENERIC_INTERNAL_ERROR],
        errorCode: error?.errorCode,
        error,
        shouldPromptForSymantec: this.shouldPromptForSymantec,
      });
    });
  }

  /**
   * Intercept password reset submission
   */
  protected onPasswordReset() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const {
      oktaSignIn: { authClient },
    } = this;
    const originalProceed = authClient.idx.proceed.bind(authClient);

    authClient.idx.proceed = async (...args: any) => {
      this.setupPasswordResetErrorHandling();

      AmplitudeUtils.logCustomEvent('PasswordReset::PasswordChangeInitiated', {
        username: getAnetUsername(),
        shouldPromptForSymantec: self.shouldPromptForSymantec,
      });

      const { step } = args[0];
      if (step === 'reset-authenticator') {
        const {
          credentials: { passcode },
          stateHandle,
        } = args[0];
        const symantecSecurityCode = this.getSymantecSecurityCode();
        const response = await self.triggerPasswordResetRequest({
          symantecSecurityCode,
          newPassword: passcode,
          stateHandle,
        });

        // Check for success flag or MFA requirement in the IDX response
        // - Success flag is present when password reset completes without additional authentication
        // - MFA requirement occurs when Okta Identity Engine transitions to LOGIN mode after password reset
        //   and prompts for additional authentication based on risk assessment (common for EPCS users)
        const requestSucceeded = !!response.success;
        const requiresMfa = response.remediation?.value?.some(
          (r: any) => r.name === 'select-authenticator-authenticate' || r.name === 'select-authenticator-enroll'
        );

        // Treat MFA requirement as a successful password update
        // The widget will handle the MFA flow automatically
        // (Need to confirm with PO to see if we want to redirect users to the login page when MFA is required)
        const passwordUpdateSucceeded = !!requestSucceeded || !!requiresMfa;
        if (passwordUpdateSucceeded) {
          AmplitudeUtils.logCustomEvent('PasswordReset::PasswordChangeSuccess', {
            username: getAnetUsername(),
            shouldPromptForSymantec: self.shouldPromptForSymantec,
          });
        }

        const idxResponse = await authClient.idx.makeIdxResponse(response, {}, passwordUpdateSucceeded);
        await authClient.transactionManager.saveIdxResponse({
          rawIdxResponse: idxResponse,
          requestDidSucceed: passwordUpdateSucceeded,
          stateHandle,
        });
        return idxResponse;
      }

      return originalProceed.apply(authClient, args);
    };
  }

  /**
   * Check if EPCS user needs Symantec VIP prompt
   */
  protected async handleSymantecPrompt() {
    const checkPayload = this.getSymantecPromptPayload();
    this.shouldPromptForSymantec = await this.shouldPromptForSymantecVIP(checkPayload);

    if (this.shouldPromptForSymantec) {
      this.customizeForEpcs();
      AmplitudeUtils.logCustomEvent('PasswordReset::SymantecVipPrompted', {
        username: getAnetUsername(),
        userId: getOktaUserId(),
      });
    }
  }

  protected async shouldPromptForSymantecVIP(payload: SymantecPromptPayload): Promise<boolean> {
    // Call lambda endpoint which validates username against stateHandle
    const response = await shouldPromptForSymantecVIPLambda({
      username: payload.username,
      stateHandle: payload.stateHandle,
    });
    return response.shouldPrompt;
  }

  protected getSymantecPromptPayload(): SymantecPromptPayload {
    return {
      username: getUsername(),
      stateHandle: getStateHandle(),
    };
  }

  protected async triggerPasswordResetRequest({ newPassword, symantecSecurityCode, stateHandle }: any): Promise<any> {
    try {
      // Call lambda endpoint which handles both password reset and cleanup
      // Add SupportEnv as a fail safe to check production user block
      // in support environment when auth server id is missing.
      const response = await userPasswordReset({
        username: getUsername(),
        newPassword,
        stateHandle,
        symantecSecurityCode,
        supportEnv: settings.supportEnv || '',
      });
      return response;
    }
    catch (error: any) {
      // Return error in format expected by widget
      if (isRetryableError(error)) {
        return submitLegacyPasswordResetForm(getAnetUsername());
      }
      if (error?.errorCode === 'password.reset.environment.error') {
        hideWidgetForm(false, true);
        this.blockPasswordUpdateForProdUser();
      }
      // Throw error to show message on the error banner
      throw getTransformedError(error);
    }
  }



  /**
   * Add Symantec VIP code field for EPCS users
   */
  protected customizeForEpcs() {
    const headerDescription =
      'Two-factor authentication is required to change your password or manage your credentialed devices.';
    this.eventEmitter.emitEventToComponent({
      componentName: HeaderWithEventHelper.displayName as string,
      eventName: 'update-header-description',
      eventPayload: { headerDescription },
    });
    const container = createWrapper({ hidden: false });
    this.getSymantecCodeTarget().before(container);
    const component = <SymantecCode />;
    this.renderReactComponent({ id: 'symantec-token', component, container });
  }

  protected getSymantecCodeTarget(): JQuery {
    return jQueryCourage('#athena-credentials\\.passcode-input-field-container');
  }

  protected getSymantecSecurityCode() {
    if (this.shouldPromptForSymantec) {
      return (document.getElementsByName('credentials.securitycode')[0] as HTMLInputElement).value;
    }
  }

  protected isSecurityCodeValid() {
    const { shouldPromptForSymantec } = this;
    const emptyFieldErrorMessage = ERROR_MESSAGES['error.field.empty'];
    if (shouldPromptForSymantec && !this.getSymantecSecurityCode()) {
      this.setErrorForSymantecField({ errorMessage: emptyFieldErrorMessage });
      return false;
    }
    return true;
  }

  private setErrorForSymantecField({ errorMessage }: { errorMessage: string }) {
    this.eventEmitter.emitEventToComponent({
      componentName: SymantecCode.displayName as string,
      eventName: 'update-error',
      eventPayload: { errorMessage },
    });
  }

  protected clearPasswordResetForm() {
    (document.getElementsByClassName('o-form o-form-edit-mode')[0] as HTMLFormElement).reset();
    this.clearSymantecCode();
  }

  protected clearSymantecCode() {
    this.eventEmitter.emitEventToComponent({
      componentName: SymantecCode.displayName as string,
      eventName: 'clear-value',
    });
  }

  protected isSymantecValidationError(errorCode: string) {
    return errorCode?.startsWith('symantec.');
  }

  protected blockPasswordUpdateForProdUser() {
    const wrapper = createWrapper({ hidden: false });
    const formContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-content"]');
    formContainer.append(wrapper);
    this.renderReactComponent({
      id: 'block-prod-user-in-support',
      container: wrapper,
      component: <BlockProdUserPasswordResetInSupportBanner />,
    });
    stopSpinner();
  }
}
